/**
 * Build the DB operations needed to end a season.
 *
 * Pure function — no hooks, no side effects.
 */

import { generatePhases } from '~/crop/crop-modal/generators/generate-preview-schedule-data'

/**
 * @param {Object} params
 * @param {string} params.nextSeasonId - Pre-created next season ID
 * @param {Array} params.cropsToKeep - Crop objects to carry forward into the next season
 * @param {string} params.createdByUserId - User performing the end-season action
 * @returns {{ newSchedules: Array }}
 */
export const buildSeasonEndOperations = ({ nextSeasonId, cropsToKeep, createdByUserId }) => {
  const newSchedules = cropsToKeep.map(crop => ({
    crop_id: crop.id,
    season_id: nextSeasonId,
    created_by: createdByUserId,
  }))

  return { newSchedules }
}

/**
 * Build phase insert records for a single carried-over crop schedule.
 *
 * All carried-over crops are treated as perennial non-first-year regardless of lifecycle:
 *   opening dormant (Jan 1 → LFD) → maturing → mature → closing dormant (after harvest → Dec 31)
 *
 * Falls back to sensible defaults when archetype data is missing.
 *
 * @param {Object} params
 * @param {string} params.scheduleId - Newly created schedule's DB id
 * @param {Object} params.crop - Crop object (with crop_details)
 * @param {Object|null} params.archetype - Full archetype record, or null for defaults
 * @param {{ lfd: Date, ffd: Date }} params.frostDates - Frost dates for the next season's year
 * @param {Array} params.phaseTypes - Phase type records from DB (for duration lookup)
 * @returns {Array} Phase records ready for bulk insert
 */
export const buildCarryoverSchedulePhases = ({ scheduleId, crop, archetype, frostDates, phaseTypes }) => {
  // Planting date = last frost date (± archetype indoor offset, already factored into transplant)
  const plantingDate = new Date(frostDates.lfd)

  // Transplant date: last frost + outdoor_move_offset weeks (0 if no archetype)
  const outdoorOffsetWeeks = archetype?.outdoor_move_offset ?? 0
  const transplantDate = new Date(frostDates.lfd)
  transplantDate.setDate(transplantDate.getDate() + outdoorOffsetWeeks * 7)

  // DTM start depends on archetype flag; harvest = DTM start + dtm days
  const dtm = archetype?.dtm ?? 60
  const dtmStartDate = archetype?.dtm_starts_at_transplant ? transplantDate : plantingDate
  const harvestDate = new Date(dtmStartDate)
  harvestDate.setDate(harvestDate.getDate() + dtm)

  const growthType = crop.crop_details?.growth_code ?? 'determinate'
  const harvestWindow = archetype?.harvest_window ?? null

  // Generate phases: perennial non-first-year always gets opening dormant + main phases + closing dormant
  const previewPhases = generatePhases(
    plantingDate,
    transplantDate,
    harvestDate,
    phaseTypes,
    null,   // null = use defaults (perennial non-first-year → ['maturing', 'mature', 'dormant'])
    {
      growthType,
      harvestWindow,
      firstFrostDate: frostDates.ffd,
      propagationCode: 'transplant',
      lifecycleType: 'perennial',
      isFirstYear: false,
    }
  )

  // Map preview phase shape → DB insert shape
  return previewPhases.map(phase => ({
    schedule_id: scheduleId,
    phase_type_code: phase.phase_type_code,
    est_started_at: phase.est_started_at,
    est_duration: phase.est_duration,
    step: phase.step,
    state: 'scheduled',
  }))
}
